package org.multiplatform.kotfolio.pages.koin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources

@Page("basic")
@Composable
fun KoinBasicPage() {
    BlogLayout(
        PageProperties(
            "Koin",
            "Koin is a lightweight pure kotlin framework designed to be straight forward in terms of usage and execution. Koin supports both Dependency Injection (DI) and the Service Locator (SL) pattern, offering flexibility to developers. Koin compatible with android, iOS, backend and web, it prioritizes clean and readable code."
        ),
        BlogProperties("Koin", "Basic")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Span(Modifier.display(DisplayStyle.Contents).toAttrs())
            {
                SpanText(
                    text = "Dependency injection is an object-oriented paradigm design pattern where objects are passed to a class from an external source, rather than the class having to create the object it needs itself internally. ",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "Dependency injection is an implementation of the well-known inversion of control principle.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "Dependency injection can either be performed manually, or it can be managed using a framework, which will either provide the required dependencies at compile time or at run time.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "Compatible with Android, backend, kobWeb and iOS, it prioritizes clean and readable code.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                SpanText(
                    text = "Manual dependency injection is also known as composition root pattern.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "Dependency Injection (DI) work in android",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency Injection is a design pattern that promotes loose coupling between classes.  In essence, it’s a technique where one object (or static method) provides the dependencies of another object. Rather than creating its own dependencies, an object receives them from an external source.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Android, DI frameworks like Dagger or Koin are commonly used to manage dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DI involves creating interfaces or abstract classes for dependencies, which are then injected into the dependent classes during runtime. This approach improves code maintainability, testability, and allows for easy swapping of dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DI works by externalizing the creation and management of an object's dependencies from the object itself. Instead of an object creating its own dependencies, these dependencies are injected or provided by an external framework or system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When using DI, the objects that require dependencies have these dependencies injected into them, typically through a constructor or a setter method. The dependency injection framework is responsible for creating the required dependencies and providing them to the object that needs them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is dependency injection used for?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency injection is used to make a class independent of its dependencies or to create a loosely coupled program.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency injection is useful for improving the reusability of code. Likewise, by decoupling the usage of an object, more dependencies can be replaced without needing to change class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency injection also aids in following the SOLID principles of object-oriented design.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dependency Injection in build upon the concept of Inversion of Control.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency Injection in build upon the concept of Inversion of Control which says that a class should get its dependencies from outside. In simple words, no class should instantiate another class but should get the instances from a configuration class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consider, that when we have to create a lot of objects which are dependent on many other objects in our project, it becomes tough when the project becomes bigger.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A dependency occurs when an object from one class requires an object from another class in order to function properly. These dependencies are often member variables of the class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We have two activities, Activity A and Activity B. Both require an object Downloader, in which Downloader will require the request. Now, the request will depend upon Executor and HTTPClient.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dependency Injection vs Service Locator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One thing to note is out of the three, technically only Dagger uses a dependency injection (DI) pattern; Koin and Kodein are service locators. In the service locator pattern, there’s a service locator class that creates and stores dependencies, then provides them on demand. Classes have control and ask for objects to be injected, whereas in dependency injection, the app has control and proactively injects the required objects into classes. Fundamentally, both patterns tackle the same problem: decoupling classes from concrete dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Let’s look at an example of injecting a Door object into House classes, then adding instances of the House classes to our main application. This isn’t meant to be a complete setup tutorial, since lots of those exist for all three libraries already, but I’ll provide some links to good existing ones.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How Koin Balances DI and SL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Global Context",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "By default, Koin provides a globally accessible component that acts like a service locator. This allows you to retrieve dependencies from a central registry using KoinComponent or inject functions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Isolated Components",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Although Koin encourages the use of Dependency Injection, particularly constructor injection, it also allows for isolated components. This flexibility means you can configure your application to use DI where it makes the most sense while still taking advantage of SL for specific cases.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SL in Android Components",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In Android development, Koin often uses SL internally within components such as Application and Activity for ease of setup. From this point, Koin recommends DI, especially constructor injection, to manage dependencies in a more structured way. However, this is not enforced, and developers have the flexibility to use SL if needed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Koin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "koin-android - Android features (common android parts)",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "koin-androidx-compose - features related to Jetpack Compose (feature module)",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "koin-androidx-workmanager - WorkManager features (common data sync module)",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "koin-core — pure Kotlin components (for Kotlin only common module)",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "koin-test - verification and testing parts",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Span(Modifier.display(DisplayStyle.Contents).toAttrs()) {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin is a pure Kotlin framework, designed to be straight forward in terms of usage and execution. It easy to use and doesn’t impact your compilation time, nor require any extra plugin configuration.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin is a lightweight dependency injection framework for Kotlin that simplifies the process of managing dependencies in your KMM project. It enables you to decouple your code and makes it easier to test and maintain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin is a Kotlin-native dependency injection framework using a DSL instead of annotations. It's lightweight, uses runtime resolution, and requires no code generation.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DI frameworks that rely on complex configurations or annotations, Koin uses a straightforward DSL (Domain Specific Language) to define modules and dependencies.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin supports both DI and the Service Locator pattern which is an injection method at runtime offering flexibility to developers.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency injection is design pattern used to managed the dependency between different component in an application. The Process of passing dependencies into a class rather than the class creating them itself.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Choose Koin/Hilt",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin when you want a clean Kotlin codebase, minimal boilerplate, and fast iteration. Choose Hilt when compile-time guarantees and tight Jetpack integration are your priority.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Koin for Compose Multiplatform?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Pure Kotlin implementation",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No annotation processing or code generation",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Seamless integration with ViewModels",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Platform-specific dependency support through expect/actual",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Kodein",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kodein is a dependency injection (DI) library for Kotlin. It's designed to simplify dependency management in Kotlin applications across multiple platforms: Android, iOS, Web, and Backend. Thanks to its simple syntax and straightforward configuration.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is KSP?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KSP is also required, as it enables the code generation used by Koin Annotations. After adding the dependencies, run a Gradle sync to make them available to the project.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Benefits of KSP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Speed: Processes Kotlin code faster than kapt.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin-First: Works directly with Kotlin language constructs, avoiding Java-based abstractions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lightweight: Reduces boilerplate and integrates seamlessly with Gradle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compatibility: Many popular libraries now support KSP natively.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin DSL: Dependencies are defined using a Kotlin Domain Specific Language (DSL).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flexibility: Koin supports lazy injection, scoped components, and test-friendly configurations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No Compile-Time Overhead: Since Koin doesn’t rely on annotation processing, it doesn’t add compile-time overhead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "KSP Processor Logic",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The processor can dynamically generate a Builder class based on the properties of the annotated data class. You need to create a class extending SymbolProcessor where all the work will be done in the process function, and a class extending SymbolProcessorProvider, which will provide the implementation of the SymbolProcessor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compile Time Checks",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    ksp {
                        arg("KOIN_CONFIG_CHECK","true")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "NoDefinitionFoundException",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The NoDefinitionFoundException in Koin (Kotlin Dependency Injection library) means that Koin couldn't find a definition (a registered dependency) for the class or type you're trying to inject or retrieve.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Pros",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lightweight: Koin is known for its simplicity and lightweight nature. It’s easy to set up and use.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin-Friendly: Koin is built with Kotlin in mind, leveraging its features such as DSLs and extension functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No Code Generation: Koin does not require code generation, which can simplify the build process and reduce compilation times.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Easy Testing: Koin’s design makes it relatively easy to write unit tests for code that uses its dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Cons",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Runtime Dependency Resolution: Dependencies are resolved at runtime, which may lead to potential issues that could have been caught at compile time with other DI frameworks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Limited Scope Support: Koin’s scoping capabilities are not as extensive as some other DI libraries, which might be a limitation for complex projects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Limited Java Support: While Koin works with Java, its design is more tailored to Kotlin, so Java developers might not find it as intuitive.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why choose Koin?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Simple and Developer-Friendly: Koin's clean DSL, no compile-time overhead, minimal setup, and easy testing let you focus on building your apps.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scales with Your App: from small apps to complex projects, Koin scales effortlessly to meet your needs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Evolving Compile-Time Safety: With features like module validation (Verify API), Koin Annotations (KSP for configuration safety), and the upcoming Koin IDE Plugin, Koin simplifies coding while boosting safety.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ready for Kotlin Multiplatform: Koin seamlessly manages dependencies across iOS, Android, Desktop, and Web, making it the go-to DI framework for cross-platform development.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Perfect for Compose Multiplatform: Koin integrates effortlessly with Compose Multiplatform, supporting shared logic and DI for UI components - even ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Setup Process",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Define modules - Create modules with single{} for singletons or factory{} for new instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Start Koin in Application - Use startKoin{} with androidContext() and modules()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inject dependencies - Use get() for direct retrieval or by inject() for lazy injection",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Create the Koin initializer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create a class that implements the Initializer interface from App Startup. This class will be responsible for initializing Koin when the application starts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Koin Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin works on a simple DSL model.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin operates on the principle of declaring and injecting dependencies into the application. It uses a module to define the dependencies and a startKoin block to initialize them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once the libraries are added, one can start defining the dependencies. Koin uses modules to define dependencies. In this module all the dependent objects are there, then we have to load one or more modules in Koin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Then, we are ready to use this object. Generally, we are loading the module into Koin in the application class by calling startKoin method, then we can inject the object wherever we want, this how the Koin works.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A module is a collection of dependencies that can be used together. Within a module, you can declare various types of components based on their lifecycle and usage. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.KOIN_WORK,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "koin work"
            )

            SpanText(
                text = "DI has normally two types",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Constructor Injection: In this type, we pass the dependency into the constructor of the class. By declaring a dependency in your class constructor, you can inject it effortlessly. However, in this scenario, it is essential to inform Koin about the instantiation of your class by defining it within the dependency module. Dependency Injection (DI) where dependencies of a class are provided through its constructor. This means that when you create an instance of a class, you explicitly pass the required dependencies to its constructor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Field Injection: Sometimes in some frameworks like activities and fragments in Android are provided by the system then we cannot pass the dependencies to their constructors that’s why we use Field Injection which is another type of DI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Interface Injection: Interface Injection is a specific type of DI where you inject an interface instead of a concrete implementation. This provides even greater flexibility and allows you to swap out implementations at runtime without affecting the client code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Setter Injection: Setter Injection is a dependency injection technique where dependencies are provided to a class through setter methods. This means that after an object is created, its dependencies can be injected by calling specific methods designed for this purpose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Property Injection: To inject dependencies into your classes, you can use the ‘by inject()’ delegate provided by Koin. You simply declare a property with the desired type and use the ‘by inject()’ delegate to initialize it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //userService will be automatically initialized with the instance provided by Koin.

                    private val userService: UserService by inject()
                    """.trimIndent()
            )

            SpanText(
                text = "Koin Dependencies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin allows you to perform dependency injection not only within the shared module but also within the Android project itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This means you can also utilize the Koin framework natively within your Android project. Therefore, you will define the dependencies for the shared module within the shared module itself, while the dependencies specific to the Android app will be defined within the Android module.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Koin, dependencies are defined using modules. A module contains the definitions of dependencies and how to create instances of them. You can define a module using the module function and provide a set of dependencies using the single or factory functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dependency Injection in Kotlin Multiplatform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "Android",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "On Android, consuming Koin is seamless. You start it in your Application class and inject wherever you need using by inject() or get(), with KoinComponent and Kotlin extension functions working naturally.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "startKoin function is pivotal for initializing the Koin dependency injection framework in your Android application. Within the body of startKoin, we set up the Koin context and register the modules that provide your dependencies.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "androidContext: This function sets the Android context for Koin. It takes the application context as a parameter and makes it available throughout the Koin container, ensuring that Koin can manage dependencies effectively within the Android environment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "ios",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "`KoinComponent` is a marker interface provided by Koin. When you implement it, you gain access to Koin’s dependency resolution functions, primarily `get()`.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "KoinComponent and Kotlin extension functions do not translate cleanly through the Swift/Obj-C bridge, which means you cannot rely on Koin’s typical injection mechanisms from the iOS side. You need a way to expose your injected objects to Swift without relying on constructs it does not understand.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ensure that you call the initKoin method in the platform-specific code, such as in the Android Application class or iOS entry point.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "To invoke the initKoin function in your iOS project, navigate to the MainViewController class within the iosMain source set and ensure that you call initKoin.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val viewmodelModule = module {
                        factory { SplashViewModel(get(), get()) }
                    }
                    
                    //iosMain
                    //1 `object` keyword creates a singleton. The bridge is stateless it just provides access to Koin so we don’t need multiple instances.
                    object PresenterProvider: KoinComponent {
                        fun provideSplashViewModel(): SplashViewModel = get()
                    }
                    
                    //2
                    class SharedDi : KoinComponent {
                        init {
                            startKoin {
                                modules(sharedModule)
                            }
                        }

                        fun sharsedPrinter(): SharedPrinter = get()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "init koin latest way",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Grippo-app/grippo-mobile.git
            KotlinCode(
                code = """
                        public object Koin {
                            public fun init(
                                appDeclaration: KoinAppDeclaration = {},
                            ): KoinApplication = KoinPlatformTools.defaultContext().startKoin {
                                appDeclaration()
                                modules()
                            }
                        }        
                    """.trimIndent()
            )

            SpanText(
                text = "Why this design?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )


            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "initKoin() is the single entry point for starting Koin. Every platform funnels through it, so core modules like networkModule are guaranteed to be loaded regardless of the target.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The appDeclaration lambda lets each platform inject its own configuration (Android context, logging, platform-specific modules) before the shared modules are loaded.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "additionalModules is how feature modules flow in from the shared layer — keeping the core module decoupled from feature modules.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/imrun/TheSliide.git
            KotlinCode(
                code = """
                        private val sharedModule = module {
                            single {
                                Json {
                                    ignoreUnknownKeys = true
                                    explicitNulls = false
                                    isLenient = true
                                }
                            }
                            single<HttpClient> {
                                HttpClient(get<HttpClientEngineFactory<*>>().create()) {
                                    expectSuccess = true
                                    install(ContentNegotiation) {
                                        json(get())
                                    }
                                    install(Logging) {
                                        logger = object : Logger {
                                            override fun log(message: String) = Unit
                                        }
                                        level = LogLevel.NONE
                                    }
                                }
                            }
                        }

                        private var koinApplication: KoinApplication? = null

                        fun initKoin(appDeclaration: KoinAppDeclaration = {}): KoinApplication {
                            koinApplication?.let { return it }
                            return startKoin {
                                appDeclaration()
                                modules(sharedModule, platformModule)
                            }.also { koinApplication = it }
                        }

                        expect val platformModule: org.koin.core.module.Module
                        
                        //androidMain
                        class MainActivity : ComponentActivity() {
                            override fun onCreate(savedInstanceState: Bundle?) {
                                enableEdgeToEdge()
                                super.onCreate(savedInstanceState)
                                initKoin {
                                    modules(
                                        module {
                                            
                                        }
                                    )
                                }
                                
                                setContent {
                                    App()
                                }
                            }
                        }
                        
                        //iosMain
                        fun MainViewController() = ComposeUIViewController {
                            initKoin()
                            App()
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //New Way
                        
                        // Declare your module & configuration
                        @Module
                        @Configuration
                        class DataModule {
                            @Singleton fun db() = Database()
                        }
                        
                        @KoinApplication 
                        cLass MyApp()
                        
                        // Run your Koin app!
                        MyApp.startKoin()
                    """.trimIndent()
            )

            //2. https://github.com/pitampoudel/komposeauth.git
            //3. https://github.com/joreilly/FantasyPremierLeague
            KotlinCode(
                code = """
                        //1
                        import org.koin.core.context.startKoin
                        import org.koin.dsl.KoinAppDeclaration
                        import org.koin.dsl.includes

                        fun initKoin(config : KoinAppDeclaration? = null){
                            startKoin {
                                printLogger()
                                includes(config)
                                modules(appModule)
                            }
                        }
                        
                        //2
                        /**
                         * Initialize the ComposeAuth dependencies.
                         * @param app The Koin application.
                         * @param httpClient The http client with ComposeAuth installed
                         */
                         
                        fun initializeComposeAuth(
                            app: KoinApplication? = null,
                            httpClient: HttpClient
                        ) {
                            val module = module {

                            }

                            app?.let {
                                app.modules(module)
                            } ?: run {
                                startKoin { modules(module) }
                            }
                        }
                        
                        //3
                        val viewModelModule = module {
                            viewModelOf(::PlayerListViewModel)
                        }
                        
                        fun initKoin(enableNetworkLogs: Boolean = false, appDeclaration: KoinAppDeclaration = {}) =
                            startKoin {
                                appDeclaration()
                                modules(commonModule(enableNetworkLogs = enableNetworkLogs))
                            }

                        // called by iOS etc
                        fun initKoin() = initKoin(enableNetworkLogs = false, appDeclaration = {})

                        fun commonModule(enableNetworkLogs: Boolean) = module {
                            single { createJson() }
                            single { createHttpClient(get(), get(), enableNetworkLogs = enableNetworkLogs) }

                            single { FantasyPremierLeagueRepository() }
                            single { FantasyPremierLeagueApi(get()) }

                            single { AppSettings(get()) }

                            includes(viewModelModule)
                            includes(platformModule())
                        }

                        fun createJson() = Json { isLenient = true; ignoreUnknownKeys = true }

                        fun createHttpClient(httpClientEngine: HttpClientEngine, json: Json, enableNetworkLogs: Boolean) = HttpClient(httpClientEngine) {
                            install(ContentNegotiation) {
                                json(json)
                            }
                            if (enableNetworkLogs) {
                                install(Logging) {
                                    logger = Logger.DEFAULT
                                    level = LogLevel.NONE
                                }
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/steve1rm/SpendLess.git
            KotlinCode(
                code = """
                        import org.koin.core.context.startKoin
                        import org.koin.core.module.Module
                        import org.koin.dsl.KoinAppDeclaration

                        fun initializeKoin(config: KoinAppDeclaration? = null, vararg platformSpecificModules: Module = emptyArray()) {
                            startKoin {
                                config?.invoke(this)
                                modules(
                                    spendLessModule,
                                    *platformSpecificModules
                                )
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "init koin dynamic way",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/enmanuel52/CornTime.git
            KotlinCode(
                code = """
                        //loadKoinModules() is a Koin function used to dynamically load additional modules at runtime, after Koin has already been started.
                        
                        val appModule = module {
                            val modules =
                                ucModule + remoteModule + remoteDsModule + featuresModule +
                                        preferencesModule + systemModule

                            loadKoinModules(
                                modules = modules,
                            )
                        }
                        
                        /**
                         * Initializes Koin with the provided configuration and modules.
                         *
                         * @param config A lambda that allows further configuration of the Koin application.
                         *              It defaults to an empty configuration.
                         */
                        fun initKoin(config: KoinAppDeclaration = {}) = startKoin {
                            config(this)
                            modules(appModule)
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "properties",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // ═══════════ KOIN PROPERTIES (external config) ═══════════
                    startKoin {
                        properties(mapOf(
                            "BASE_URL" to "https://api.mybank.com/v1/",
                            "DB_NAME" to "banking.db",
                            "TIMEOUT" to "30"
                        ))
                        modules(appModules)
                    }

                    // Use in module
                    single {
                        Retrofit.Builder()
                            .baseUrl(getProperty("BASE_URL"))
                            .build()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Scope Archetype",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        module {
                            requestScope {
                                // Definitions scoped to an HTTP request
                            }
                        }

                        module {
                            activityScope {
                                // Definitions scoped to an Activity
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No KSP plugin, no kapt, no koin-android artifact — koin-compose-viewmodel's koinViewModel() already handles Android's ViewModel/SavedStateHandle integration, and since Koin is started through a shared Compose entry point (more on that below) rather than Application.onCreate(), koin-android's Android-context helpers (androidContext(), androidLogger()) aren't needed at all.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModels are architecture components designed to survive configuration changes and manage UI-related data. Koin provides special support for ViewModels with lifecycle-aware injection. Koin ViewModel DSL is fully multiplatform via koin-core-viewmodel. For Compose Multiplatform. Koin-Android convenience DSL that wraps factory with Android ViewModel lifecycle awareness.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://blog.insert-koin.io/injecting-your-compose-applications-with-koin-c9c8b1166702
            SpanText(
                text = "koinInject() — Non-ViewModel Dependencies in Composables",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For injecting regular dependencies (repositories, HTTP clients, etc.) inside @Composable functions.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Work in compose multiplatform for desktop. To inject dependencies inside a Composable function, you need to use the koinInject() function, which can also be used within the function body. The koinInject function will use the cached data from your Composable. other words, a simple factory instance requested inside a Composable will remain attached to it until it's dropped.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "by inject() — Lazy Injection via KoinComponent",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For classes that aren’t Composables and aren’t constructed by Koin:",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class MyViewModel : ViewModel(), KoinComponent {
                        private val myUseCase: MyUseCase by inject()
                        private val myRepository: MyRepository by inject()
                    }
                    
                    // Android-specific
                    actual class MyManagerImpl : MyManager, KoinComponent {
                        private val context: Context by inject()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "koinViewModel()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State holder — will be injected at Composable level to handle local logic. ViewModel — will be injected at Screen level to handle more global business logic. koinViewModel() to inject a viewModel. koinViewModel() manages lifecycle by integrating with Android’s ViewModelStoreOwner; koinInject() does not.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does koin internally manage object lifecycle?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "singleton registry, factory creation policy, scope lifetime, external owner lifecycle (ViewModels)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //2 and 3 https://github.com/Mikkareem/DosthMan.git
            KotlinCode(
                code = """
                    //1
                    fun DisplayWeatherDetails(viewModel: WeatherViewModel = koinViewModel()) {
                        val uiState by viewModel.weatherResponseState.collectAsState()
                    }
                    
                    //2 Parameter key
                    val viewModel = koinViewModel<TabViewModel>(
                      key = ",
                      parameters = {  }
                    )
                    
                    //3
                    @Composable
                    fun rememberOnAction(tabKey: String): (TabAction) -> Unit {
                        if (LocalInspectionMode.current) return {}

                        val viewModel = koinViewModel<TabViewModel>(
                            key = tabKey,
                            parameters = { parametersOf(tabKey) }
                        )

                        val latestAction = rememberUpdatedState(viewModel::onAction)

                        return remember {
                            { action -> latestAction.value(action) }
                        }
                    }
                    
                    internal val LocalInspectionTabState = compositionLocalOf<TabState> { error("No value passed for LocalInspectionTabState") }

                    @Stable
                    data class TabState(
                        val requestResponse: UiRequestResponse? = null,
                        val urlState: TextFieldState = TextFieldState(),
                        val paramsState: List<Pair<TextFieldState, TextFieldState>> = emptyList(),
                        val headersState: List<Pair<TextFieldState, TextFieldState>> = emptyList(),
                        val bodyContentState: TextFieldState = TextFieldState(),
                        val bodyTypeState: TextFieldState = TextFieldState(),
                    )
                    
                    //4 Lazy inject Presenter
                    private val presenter: Presenter by inject()
                      
                    //5 Lazy inject ViewModel
                    private val viewModel: UserViewModel by viewModel()
                    """.trimIndent()
            )

            SpanText(
                text = "Kotlin context parameters: not DI, but execution context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://medium.com/@kamil.jedrzejuk/kotlin-context-parameters-not-di-but-execution-context-a768ad96fa37
            //https://github.com/CamilYed/kotlin-context-parameters-ddd
            KotlinCode(
                code = """
                    //https://github.com/CamilYed/kotlin-context-parameters-ddd
                    
                    interface ClockProvider {
                        fun now(): Instant
                    }
                    
                    fun confirmedAt(clock: ClockProvider): Instant = clock.now()
                    
                    class OrderService(
                        private val clock: ClockProvider,
                    ) {
                        fun confirmedAt(): Instant =
                            clock.now()
                    }
                    
                    context(clock: ClockProvider)
                    fun confirmedAt(): Instant = clock.now()
                    
                    val clock = FixedClockProvider(
                        Instant.parse("2026-06-05T10:15:30Z"),
                    )
                    
                    context(clock) {
                        confirmedAt()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "parameterOf - Inject ViewModel With Runtime Arguments",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inject ViewModel With Runtime Arguments Use it when the ViewModel itself depends on runtime parameters, and you want DI to handle construction cleanly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Injecting a ViewModel that requires orderId immediately (runtime):",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/dreamcodervw/OttCmp.git
            KotlinCode(
                code = """
                    @Composable
                    inline fun <reified T : Any> rememberKoinInject(
                        key: Any? = null,
                        noinline parameters: ParametersDefinition? = null,
                    ): T {
                        val koin = getKoin()
                        return remember(key) {
                            if (parameters != null) koin.get(parameters = parameters) else koin.get()
                        }
                    }

                    private val presentationModule = module {
                        factory { (movieId: Int) -> MovieDetailsViewModel(movieId = movieId)) }
                    }
                    
                    class MovieDetailsViewModel(
                        private val movieId: Int
                    ) : BaseViewModel() { }
                    
                    //Navigation
                    val viewModel: MovieDetailsViewModel = rememberKoinInject(key = s.id) { parametersOf(s.id) }
                    MovieDetailScreen(
                        viewModel = viewModel,
                        onBack = { screen = Screen.Home },
                    )
                            
                    @Composable
                    fun MovieDetailScreen(
                        viewModel: MovieDetailsViewModel,
                        onBack: () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {}
                    """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP.git
            KotlinCode(
                code = $$"""
                    internal class PhotoDetailViewModel(
                        photoId: String,
                    ) : ViewModel(), KoinComponent {  }
                    
                    private fun viewModelModule() = module {
                        viewModelOf(::HomeScreenViewModel)
                        viewModel {
                            PhotoDetailViewModel(
                                photoId = it.get<String>()
                            )
                        }
                    }
                    
                    @Composable
                    internal fun PhotoDetailScreenEntryPoint(
                        planetId: String,
                        viewModel: PhotoDetailViewModel = koinViewModel<PhotoDetailViewModel> { parametersOf(photoId) },
                       
                        //key is used when you need multiple instances of the same ViewModel class in the same scope
                        //viewModel: PhotoDetailViewModel =  koinViewModel(key = "photAdmin_$photoId" ) { parametersOf(photoId) }
                        //viewModel: PhotoDetailViewModel =  koinViewModel(key = "photoCustomer_$photoId" ) { parametersOf(photoId) }
                    ) {
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //1. get
                    val sharedModule = module {
                        viewModel { LevelViewModel(get(), get()) }
                    }

                    val viewModel: LevelViewModel = koinViewModel { parametersOf(1) }

                    class LevelViewModel(
                        private val levelNumber: Int,
                        private val levelRepository: LevelRepository
                    ): ViewModel() {}


                    //2. Parameters get
                    //https://github.com/TaxiKab119/meals-cmp-demo/tree/main
                    val commonModule = module {
                        // Provide MealDetailViewModel with Koin's viewModel scope
                        viewModel { parameters -> MealDetailViewModel(client = get(), mealId = parameters.get()) }
                    }

                    //List Click Navigation
                    navController.navigate(DetailScreen(mealId = mealId))

                    //Detail Navigation
                    val mealDetailViewModel: MealDetailViewModel = koinViewModel { parametersOf(detailScreen.mealId) }


                    class MealDetailViewModel(
                        private val client: MealsDbClient,
                        private val mealId: String,
                    ) : ViewModel() { }

                    @Serializable
                    data class DetailScreen(val mealId: String)

                    //3 Dynamic Inject
                    //https://github.com/sajidalidev/whattowatch
                    data class MovieDetailsScreen(val movieId: Int) : Screen {
                        @Composable
                        override fun Content() {
                            val viewModel: MovieDetailsViewModel = koinInject { parametersOf(movieId) }
                        }
                    }

                    class MovieDetailsViewModel(
                        private val tmdbApi: TmdbApi,
                        private val watchlistRepository: WatchlistRepository,
                        private val movieId: Int
                    ) { }

                    object AppModule {
                        fun create(apiKey: String): Module = module {
                            factory { parameters -> MovieDetailsViewModel(get(), get(), parameters.get()) }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/steve1rm/BusbyMoviesV2 [develop branch]

                    val movieDetailModule = module {
                        viewModel { (movieId: Int) ->
                            MovieDetailViewModel(
                                movieDetailUseCase = get<MovieDetailUseCase>(),
                                configurationUseCase = get<ConfigurationUseCase>(),
                                movieCreditsUseCase = get<MovieCreditsUseCase>(),
                                similarMoviesUseCase = get<SimilarMoviesUseCase>(),
                                movieId = movieId
                            )
                        }
                    }

                    class MovieDetailViewModel(
                    private val movieDetailUseCase: MovieDetailUseCase,
                    private val configurationUseCase: ConfigurationUseCase,
                    private val movieCreditsUseCase: MovieCreditsUseCase,
                    private val similarMoviesUseCase: SimilarMoviesUseCase,
                    private val movieId: Int) : ViewModel() {

                    }

                    /** We need the movieId to be injected into the viewmodel when loading the details onStart */
                    val movieDetailViewModel: MovieDetailViewModel = koinViewModel(parameters = { parametersOf(movieId) })
                    val movieDetailState by movieDetailViewModel.movieDetailState.collectAsStateWithLifecycle()
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/@prakash_ranjan/koin-dependency-injection-in-kotlin-multiplatform-a-practical-guide-7df663b03610
//https://medium.com/@johnyalam.lut/koin-dependency-injection-in-kotlin-multiplatform-a-practical-guide-72134241d720